import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { User, getAuth, Unsubscribe, onAuthStateChanged, signInAnonymously } from "firebase/auth";
import { AppDispatch } from "app/store";
import { LoadAsset } from "app/LoadAsset";
import { useFetchUserData } from "app/hook";
import { actions as configActions } from "app/configSlice";
import { isDev } from "common/utils";
import { actions } from "features/user/slice";
import { connectEmulator } from "./api";
import { parentsFirebase } from "./Auth";

export const AuthProvider = (props: { children: React.ReactNode | null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const parentsAuth = getAuth(parentsFirebase);
  const [loading, setLoading] = useState(true);
  const [latestVersion, setLatestVersion] = useState(false);
  const { handleFetchParentsUser, handleFetchChildrenUser } = useFetchUserData();
  const [isChildrenAuthenticating, setIsChildrenAuthenticating] = useState(true);
  const [isParentsAuthenticating, setIsParentsAuthenticating] = useState(true);

  useEffect(() => {
    if (isDev) {
      const auth = getAuth();
      connectEmulator(auth, parentsAuth);
    }
  }, []);

  useEffect(() => {
    let parentsUnsubscribe: Unsubscribe;
    if (latestVersion) {
      parentsUnsubscribe = onAuthStateChanged(
        parentsAuth,
        async (user: User) => {
          if (user && !user?.isAnonymous) {
            try {
              dispatch(configActions.setLoading(true));
              handleFetchParentsUser({
                uid: user.uid,
                callback: (redirectTo: string) => {
                  if (redirectTo) {
                    navigate(redirectTo, { replace: true });
                  }
                  dispatch(configActions.setLoading(false));
                  setIsParentsAuthenticating(false);
                },
              });
            } catch (error) {
              dispatch(configActions.setLoading(false));
              console.error(error);
            }
          } else {
            if (!user) {
              await signInAnonymously(parentsAuth);
            }
            dispatch(actions.clearUser());
            setIsParentsAuthenticating(false);
          }
        },
        (error) => console.error(JSON.stringify(error)),
      );
    }

    return () => {
      if (parentsUnsubscribe) {
        parentsUnsubscribe();
      }
    };
  }, [latestVersion]);

  useEffect(() => {
    let childrenUnsubscribe: Unsubscribe;
    if (latestVersion && !isParentsAuthenticating) {
      const auth = getAuth();
      childrenUnsubscribe = onAuthStateChanged(
        auth,
        async (user: User) => {
          if (user && (!parentsAuth.currentUser || parentsAuth.currentUser?.isAnonymous)) {
            dispatch(configActions.setLoading(true));
            handleFetchChildrenUser({
              uid: user.uid,
              callback: () => {
                dispatch(configActions.setLoading(false));
                setIsChildrenAuthenticating(false);
              },
            });
          } else {
            if (auth.currentUser) {
              await auth.signOut();
            }
            setIsChildrenAuthenticating(false);
          }
        },
        (error) => console.error(JSON.stringify(error)),
      );
    }

    return () => {
      if (childrenUnsubscribe) {
        childrenUnsubscribe();
      }
    };
  }, [latestVersion, isParentsAuthenticating]);

  return (
    <>
      <LoadAsset
        isAuthenticating={isChildrenAuthenticating || isParentsAuthenticating}
        loading={loading}
        setLoading={setLoading}
        setLatestVersion={setLatestVersion}
      />
      {loading || isChildrenAuthenticating || isParentsAuthenticating ? <></> : props.children}
    </>
  );
};
